import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BrainCircuit, Check, RefreshCw, X } from 'lucide-react';
import { trainingApi } from '../services/api';
import TrainingTabs from '../components/training/TrainingTabs';

/**
 * The unit graph (EP-26 FE-6 ST-3; plan 4.6, 7): edges by kind and source, the edges waiting for
 * review, and what each campaign's next round touches -- the refresh set the round planner uses.
 * Confirming or rejecting an inferred edge needs training:operate and a person's own login;
 * releasing the draft as a new version needs training:approve.
 */
type T = Record<string, any>;
const keyOf = (e: T) => `${e.from}|${e.kind}|${e.to}|${e.source}`;

export default function TrainingRelationshipsPage() {
  const { programId = '' } = useParams<{ programId: string }>();
  const [data, setData] = useState<T | null>(null);
  const [version, setVersion] = useState('draft');
  const [next, setNext] = useState<T[]>([]);
  const [can, setCan] = useState<T>({});
  const [kind, setKind] = useState('');
  const [source, setSource] = useState('');
  const [unit, setUnit] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [message, setMessage] = useState('');
  const [report, setReport] = useState<T | null>(null);
  const [building, setBuilding] = useState(false);

  const load = useCallback(() => {
    trainingApi.unitGraph(programId, version).then(setData).catch((e) => setMessage(String(e?.message || e)));
    trainingApi.graphNext(programId).then(setNext).catch(() => setNext([]));
    trainingApi.programme(programId).then((p) => setCan(p.can || {})).catch(() => setCan({}));
  }, [programId, version]);
  useEffect(() => { load(); }, [load]);

  const edges: T[] = data?.graph?.edges || [];
  const sources = useMemo(() => [...new Set(edges.map((e) => e.source))].sort(), [edges]);
  const shown = edges.filter((e) => (!kind || e.kind === kind) && (!source || e.source === source)
    && (!unit || e.from.includes(unit) || e.to.includes(unit)) && (!pendingOnly || (e.inferred && !e.review)));
  const counts = data?.graph?.counts || {};
  const isDraft = version === 'draft';

  const decide = async (e: T, decision: 'confirmed' | 'rejected') => {
    setMessage('');
    try { await trainingApi.reviewEdge(programId, keyOf(e), decision); load(); }
    catch (err: any) { setMessage(err?.message || 'Not recorded.'); }
  };
  const release = async () => {
    setMessage('');
    try {
      const v = await trainingApi.releaseGraph(programId);
      setMessage(`Released as version ${v.version}.`); load();
    } catch (err: any) { setMessage(err?.message || 'Not released.'); }
  };

  const build = async (dryRun: boolean) => {
    setMessage(''); setBuilding(true);
    try {
      const out = await trainingApi.buildGraph(programId, dryRun);
      setReport(out.report);
      setMessage(dryRun ? 'Dry run: nothing saved.' : `Draft rebuilt: ${out.counts?.edges ?? 0} edges.`);
      if (!dryRun) load();
    } catch (err: any) { setMessage(err?.message || 'Not built.'); } finally { setBuilding(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> Model Training</h1>
        <button type="button" onClick={load} className="ml-auto text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>
      <TrainingTabs programId={programId} />

      <section className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-1">Graph
          <select value={version} onChange={(e) => setVersion(e.target.value)}
            className="border rounded px-2 py-1 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
            <option value="draft">Draft</option>
            {(data?.versions || []).map((v: T) => <option key={v.version} value={String(v.version)}>Version {v.version}</option>)}
          </select>
        </label>
        <span className="text-xs text-gray-500">
          {counts.edges ?? 0} edges over {counts.units ?? 0} units · {counts.awaitingReview ?? 0} awaiting review · {counts.rejected ?? 0} rejected
          {data?.graph?.releasedAt ? ` · released ${new Date(data.graph.releasedAt).toLocaleDateString()} by ${data.graph.releasedBy}` : ''}
        </span>
        {isDraft && can.approve && (counts.edges ?? 0) > 0 && (
          <button type="button" onClick={release} className="ml-auto px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:border-accent">
            Release the draft as a new version</button>)}
      </section>
      {isDraft && can.operate && (
        <section className="flex flex-wrap items-center gap-2 text-xs">
          <button type="button" disabled={building} onClick={() => build(true)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:border-accent disabled:opacity-50">
            Dry-run a build from the programme's sources</button>
          <button type="button" disabled={building} onClick={() => build(false)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:border-accent disabled:opacity-50">
            Rebuild the draft from them</button>
          <span className="text-gray-500">Reviews already recorded on identical edges are kept.</span>
        </section>)}
      {message && <p className="text-xs text-gray-700 dark:text-gray-300">{message}</p>}
      {report && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-xs space-y-1">
          <div className="font-semibold text-gray-900 dark:text-gray-100">Build report</div>
          <div>{report.edges} edges · {report.liveAtoms} live atoms · {report.noEdgesCount} with no edge · {report.cardUnresolved} unresolved card relations</div>
          <div>By source: {Object.entries(report.sources || {}).map(([k, v]) => `${k} ${v}`).join(' · ')}</div>
          {Object.keys(report.dropped || {}).length > 0 && <div>Dropped (target is not a unit): {Object.entries(report.dropped).map(([k, v]) => `${k} ${v}`).join(' · ')}</div>}
          {(report.unresolvedLegacy || []).length > 0 && (
            <div>Legacy ids not resolved: {report.unresolvedLegacy.map((u: T) => `${u.element}/${u.atom_id} (${u.why})`).join('; ')}</div>)}
          {(report.noEdges || []).length > 0 && (
            <details><summary className="cursor-pointer">Atoms with no edge ({report.noEdges.length})</summary>
              <div className="mt-1 font-mono break-words">{report.noEdges.join(', ')}</div></details>)}
        </section>)}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">What the next round touches</h2>
        {next.length === 0 ? <p className="text-xs text-gray-500">No campaign has a round still to train.</p> : (
          <ul className="space-y-1 text-xs">
            {next.map((n) => (
              <li key={n.campaign.id} className="text-gray-800 dark:text-gray-200">
                <Link to={`/training/${encodeURIComponent(programId)}/campaigns/${encodeURIComponent(n.campaign.id)}`} className="text-accent hover:underline">
                  {n.campaign.key || n.campaign.title}</Link>{' '}
                round {n.round}: new <strong>{n.newUnits.join(', ') || '—'}</strong>; refresh {n.refreshUnits.join(', ') || 'none'}
                <span className="text-gray-500"> ({n.refreshBasis})</span>
              </li>))}
          </ul>)}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mr-2">Edges</h2>
          <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind"
            className="border rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
            <option value="">Every kind</option>
            {(data?.kinds || []).map((k: string) => <option key={k} value={k}>{k} ({counts.byKind?.[k] ?? 0})</option>)}
          </select>
          <select value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source"
            className="border rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
            <option value="">Every source</option>
            {sources.map((s) => <option key={s} value={s}>{s} ({counts.bySource?.[s] ?? 0})</option>)}
          </select>
          <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit"
            className="border rounded px-2 py-1 text-xs w-32 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600" />
          <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} /> Awaiting review only</label>
          <span className="text-xs text-gray-500 ml-auto">{shown.length} shown</span>
        </div>
        {edges.length === 0 ? <p className="text-xs text-gray-500">No edges yet. Until a graph is released and pinned, every earlier unit is refreshed each round.</p> : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
                <tr><th className="text-left p-2">From</th><th className="text-left p-2">Kind</th><th className="text-left p-2">To</th>
                  <th className="text-left p-2">Source</th><th className="text-left p-2">Review</th></tr>
              </thead>
              <tbody>
                {shown.slice(0, 500).map((e) => (
                  <tr key={keyOf(e)} className={`border-t border-gray-100 dark:border-gray-700 ${e.review?.decision === 'rejected' ? 'opacity-50 line-through' : ''}`}>
                    <td className="p-2"><code>{e.from}</code></td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">{e.kind}</td>
                    <td className="p-2"><code>{e.to}</code></td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">{e.source}</td>
                    <td className="p-2 text-xs">
                      {e.review ? <span title={e.review.note || ''}>{e.review.decision} by {e.review.by} · {new Date(e.review.at).toLocaleDateString()}</span>
                        : e.inferred ? (isDraft && can.operate ? (
                          <span className="flex gap-1">
                            <button type="button" onClick={() => decide(e, 'confirmed')} className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 hover:border-emerald-600 flex items-center gap-0.5"><Check className="w-3 h-3" /> Confirm</button>
                            <button type="button" onClick={() => decide(e, 'rejected')} className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 hover:border-red-600 flex items-center gap-0.5"><X className="w-3 h-3" /> Reject</button>
                          </span>) : <span className="text-amber-700 dark:text-amber-400">inferred, awaiting review</span>)
                        : <span className="text-gray-500">from source</span>}
                    </td>
                  </tr>))}
              </tbody>
            </table>
            {shown.length > 500 && <p className="p-2 text-xs text-gray-500">First 500 of {shown.length}; narrow by kind, source or unit.</p>}
          </div>)}
      </section>
    </div>
  );
}
